import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import cn from 'classnames'
import { Checkbox } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { thematicsAPI } from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import cclasses from '@pages/Admin/CRM/styles/checkbox.module.css'
import FileField from '@UI/FileField'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import TextField from '@pages/Admin/CourseSettings/components/TextField/TextField'
import TiptapField from '@pages/Admin/CourseSettings/components/TiptapField/TiptapField'
import SelectField from '@pages/Admin/CourseSettings/components/SelectField/SelectField'
import { normalizeNumberInput } from '@pages/Admin/CourseSettings/components/BasicTextField/helper'
import { normalizeTiptapValue } from '@/helpers/new/normalizeTiptapValue'
import CreateFolderModal from '@pages/Admin/CourseSettings/modals/CreateFolderModal/CreateFolderModal'
import CreateSubFolderModal from '@pages/Admin/CourseSettings/modals/CreateSubFolderModal/CreateSubFolderModal'
import ClassCatalogCard from '@pages/CoursesCatalog/components/ClassCatalogCard/ClassCatalogCard'
import { ElClassFormDataType } from '../PageContent/PageContent'
import classes from './index.module.css'

interface CatalogSettingsProps {
  elClassFormData: ElClassFormDataType
  setElClassFormData: Dispatch<SetStateAction<ElClassFormDataType>>
}

interface CatalogFolder {
  id: number
  name: string
}

interface CatalogSubFolder {
  id: number
  parent_id: number
  name: string
}

const durationUnitOptions: ISelect[] = [
  { label: 'Месяцы', value: 'month' },
  { label: 'Недели', value: 'week' },
  { label: 'Дни', value: 'day' },
]

const CatalogSettings: FC<CatalogSettingsProps> = ({
  elClassFormData,
  setElClassFormData,
}) => {
  const addAlert = useAddAlert()
  const [catalogFolders, setCatalogFolders] = useState<CatalogFolder[]>([])
  const [catalogSubFolders, setCatalogSubFolders] = useState<
    CatalogSubFolder[]
  >([])
  const [loadingCatalogFolders, setLoadingCatalogFolders] = useState(false)
  const [createFolderModal, setCreateFolderModal] = useState(false)
  const [createSubFolderModal, setCreateSubFolderModal] = useState(false)

  const updateSettings = <K extends keyof ElClassFormDataType>(
    field: K,
    value: ElClassFormDataType[K],
  ) => {
    setElClassFormData((prev) => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    setLoadingCatalogFolders(true)
    Promise.all([
      thematicsAPI.getAll({
        params: {
          'filter[view][eq]': 1,
        },
      }),
      thematicsAPI.getAllSubFolders({
        params: {
          'filter[view][eq]': 1,
        },
      }),
    ])
      .then(([foldersResponse, subFoldersResponse]) => {
        setCatalogFolders(foldersResponse?.data?.data ?? [])
        setCatalogSubFolders(subFoldersResponse?.data?.data ?? [])
      })
      .catch(() => {
        addAlert({
          title: 'Ошибка',
          text: 'Не удалось загрузить активные папки каталога',
          type: 'error',
        })
      })
      .finally(() => setLoadingCatalogFolders(false))
  }, [])

  const onCatalogFoldersSelect = (value: string[] | string | null) => {
    if (!Array.isArray(value)) return
    if (value.includes('create_folder')) {
      setCreateFolderModal(true)
      return
    }

    const folderIds = value.map(Number)
    setElClassFormData((prev) => ({
      ...prev,
      catalog_folder_ids: folderIds,
      catalog_sub_folder_ids: (prev.catalog_sub_folder_ids ?? []).filter(
        (subFolderId) => {
          const subFolder = catalogSubFolders.find(
            (item) => item.id === subFolderId,
          )
          return subFolder && folderIds.includes(subFolder.parent_id)
        },
      ),
    }))
  }

  const onCatalogSubFoldersSelect = (value: string[] | string | null) => {
    if (!Array.isArray(value)) return
    if (value.includes('create_sub_folder')) {
      setCreateSubFolderModal(true)
      return
    }

    updateSettings('catalog_sub_folder_ids', value.map(Number))
  }

  const catalogFolderOptions = [
    {
      label: 'Создать папку',
      value: 'create_folder',
      'data-create-element': true,
    },
    ...catalogFolders.map((folder) => ({
      label: folder.name,
      value: folder.id.toString(),
    })),
  ]

  const catalogSubFolderOptions = [
    {
      label: 'Создать подпапку',
      value: 'create_sub_folder',
      'data-create-element': true,
    },
    ...catalogSubFolders
      .filter((subFolder) =>
        (elClassFormData.catalog_folder_ids ?? []).includes(
          subFolder.parent_id,
        ),
      )
      .map((subFolder) => ({
        label: subFolder.name,
        value: subFolder.id.toString(),
      })),
  ]

  return (
    <>
      <Checkbox
        className={cn(cclasses.checkbox, classes.checkbox)}
        checked={elClassFormData.catalog_enabled ?? false}
        onChange={(event) =>
          updateSettings('catalog_enabled', event.target.checked)
        }
      >
        Добавить класс в каталог
        <div className={classes.tooltipWrapper}>
          <TooltipWrapper
            title="Класс можно добавить в несколько папок и подпапок каталога. Если ничего не выбрано, класс сохраняется без привязки к папкам."
            className={classes.tooltipMain}
            overlayClassName={classes.tooltipOverlay}
          >
            <InfoCircleOutlined
              className={classes.tooltipIcon}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </TooltipWrapper>
        </div>
      </Checkbox>
      {elClassFormData.catalog_enabled && (
        <div className={classes.grid} style={{ marginTop: '10px' }}>
          <div className={classes.settings}>
            <div className={classes.folderGrid}>
              <SelectField
                popupClassName={classes.folderSelectField}
                label="Папки, в которых лежит класс"
                placeholder="Название"
                mode="multiple"
                allowClear
                loading={loadingCatalogFolders}
                value={(elClassFormData.catalog_folder_ids ?? []).map(String)}
                options={catalogFolderOptions}
                onChange={onCatalogFoldersSelect}
                notFoundContent="Нет активных папок"
              />
              <SelectField
                popupClassName={classes.folderSelectField}
                label="Подпапки, в которых лежит класс"
                placeholder="Название"
                mode="multiple"
                allowClear
                loading={loadingCatalogFolders}
                value={(elClassFormData.catalog_sub_folder_ids ?? []).map(
                  String,
                )}
                options={catalogSubFolderOptions}
                onChange={onCatalogSubFoldersSelect}
                notFoundContent="Нет активных подпапок"
              />
            </div>
            <TiptapField
              label="Описание класса"
              placeholder="Это описание класса, оно может быть пустым, тогда в каталоге в карточке класса не будет кнопки «Подробнее»"
              contentCN={classes.tiptap}
              value={normalizeTiptapValue(elClassFormData.description ?? '')}
              onChange={(value) => updateSettings('description', value)}
            />
            <div className={classes.fieldsGrid}>
              <TextField
                label="Длительность класса"
                value={String(elClassFormData.duration_in_months ?? '')}
                placeholder="-"
                onChange={(value) =>
                  updateSettings(
                    'duration_in_months',
                    normalizeNumberInput({
                      value,
                      min: 1,
                      max: 300,
                      empty: true,
                      allowDecimals: false,
                    }),
                  )
                }
              />
              <SelectField
                label="Единица измерения"
                value={elClassFormData.duration_unit ?? 'month'}
                options={durationUnitOptions}
                onChange={(value) => {
                  if (typeof value === 'string') {
                    updateSettings(
                      'duration_unit',
                      value as 'month' | 'week' | 'day',
                    )
                  }
                }}
                showSearch={false}
              />
            </div>
            <TextField
              label="Старая цена (Для карточки в каталоге)"
              value={String(elClassFormData.old_price ?? '')}
              placeholder="-"
              onChange={(value) =>
                updateSettings(
                  'old_price',
                  normalizeNumberInput({
                    value,
                    empty: true,
                  }),
                )
              }
            />
            <FileField
              label="Аватар класса"
              placeholder="Выберите изображение"
              value={elClassFormData.mediaIcon?.name}
              onMediaSelect={(media) => {
                updateSettings('media_icon_id', media?.id ?? null)
                updateSettings('mediaIcon', media)
              }}
              showLocalUpload={false}
              galleryView
              platformMediaTooltip="Выбрать изображение из Platform Media"
            />
          </div>
          <div className={classes.preview}>
            <ClassCatalogCard
              className={classes.previewCard}
              classValue={{
                id: elClassFormData.id ?? 0,
                title: elClassFormData.title ?? '',
                amount: elClassFormData.amount ?? null,
                one_pay: elClassFormData.one_pay ?? false,
                access_by_days: elClassFormData.access_by_days ?? false,
                custom_payment_period: elClassFormData.custom_payment_period
                  ? Number(elClassFormData.custom_payment_period)
                  : null,
                description: elClassFormData.description,
                media_icon_id: elClassFormData.media_icon_id,
                mediaIcon: elClassFormData.mediaIcon,
                duration_in_months: elClassFormData.duration_in_months,
                duration_unit: elClassFormData.duration_unit,
                old_price: elClassFormData.old_price,
              }}
              preview
            />
          </div>
        </div>
      )}
      <CreateFolderModal
        isVisible={createFolderModal}
        onDismiss={() => setCreateFolderModal(false)}
        onCreate={(folder) => {
          setCatalogFolders((prev) => [...prev, folder])
          setElClassFormData((prev) => ({
            ...prev,
            catalog_folder_ids: Array.from(
              new Set([...(prev.catalog_folder_ids ?? []), folder.id]),
            ),
          }))
          setCreateFolderModal(false)
        }}
      />
      <CreateSubFolderModal
        isVisible={createSubFolderModal}
        folderId={elClassFormData.catalog_folder_ids?.[0] ?? null}
        onDismiss={() => setCreateSubFolderModal(false)}
        onCreate={(subFolder) => {
          setCatalogSubFolders((prev) => [...prev, subFolder])
          setElClassFormData((prev) => ({
            ...prev,
            catalog_folder_ids: Array.from(
              new Set([
                ...(prev.catalog_folder_ids ?? []),
                subFolder.parent_id,
              ]),
            ),
            catalog_sub_folder_ids: Array.from(
              new Set([...(prev.catalog_sub_folder_ids ?? []), subFolder.id]),
            ),
          }))
          setCreateSubFolderModal(false)
        }}
      />
    </>
  )
}

export default CatalogSettings
